import {Component, input, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {CountryDetails, LocationCategory, LocationDetails, LocationOption} from '../../../../api';
import {Tooltip} from 'primeng/tooltip';
import {Tag} from 'primeng/tag';

interface CategoryOption {
  label: string;
  value: LocationCategory;
}

@Component({
  selector: 'manage-location-modal',
  imports: [
    Button,
    FormsModule,
    InputText,
    Select,
    Tag,
    ToggleSwitch,
    Tooltip,
  ],
  templateUrl: './manage-location-modal.component.html',
  styleUrl: './manage-location-modal.component.scss',
})
export class ManageLocationModalComponent implements OnInit {
  modalData = input<LocationDetails>();
  countries = input<CountryDetails[]>([]);

  localData: LocationDetails = {
    id: 'new',
    inRegion: false,
    enabled: true,
    names: [],
    category: undefined,
  };

  selectedDefaultName: LocationOption | undefined = undefined;
  newAltName: string = '';

  categoryOptions: CategoryOption[] = [
    {label: 'Town or City', value: LocationCategory['TownOrCity']},
    {label: 'Country', value: LocationCategory['Country']},
    {label: 'Overseas', value: LocationCategory['Overseas']},
  ];

  // Active countries, plus the location's current country so an inactive one still displays
  get countryOptions(): CountryDetails[] {
    return this.countries()
      .filter((c) => c.enabled || c.id === this.localData.countryId)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  get isOverseas(): boolean {
    return this.localData.category === LocationCategory.Overseas;
  }

  setInRegion(inRegion: boolean): void {
    if (!this.isOverseas) {
      this.localData.inRegion = inRegion;
    }
  }

  // Overseas locations need a country and are always outside the CLC region
  onCategoryChange(): void {
    if (this.isOverseas) {
      this.localData.inRegion = false;
    } else {
      this.localData.countryId = null;
    }
  }

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {}

  ngOnInit() {
    if (!!this.modalData()) {
      this.localData = {
        ...this.modalData()!,
        names: this.modalData()!.names?.map((n) => ({...n})) ?? [],
      };
      this.selectedDefaultName =
        this.localData.names?.find((n) => n.nameId === this.localData.defaultNameId) ??
        this.localData.names?.[0];
    }else{
      this.selectedDefaultName = {name: '', nameId: 'new', enabled: true};
      this.localData.names = [this.selectedDefaultName];
    }
  }

  get altNames(): LocationOption[] {
    if (!this.localData.names) return [];
    return this.localData.names.filter((n) => n !== this.selectedDefaultName);
  }

  addAltName(): void {
    if (!this.newAltName.trim()) return;
    const newOption: LocationOption = {
      name: this.newAltName.trim(),
      nameId: 'new',
      enabled: true,
      locationId: this.localData.id === 'new' ? undefined : (this.localData.id ?? undefined),
    };
    if (!this.localData.names) {
      this.localData.names = [];
    }
    this.localData.names.push(newOption);
    if (this.localData.names.length === 1) {
      this.selectedDefaultName = newOption;
    }
    this.newAltName = '';
  }

  canRemoveName(name: LocationOption): boolean {
    return name.nameId === 'new';
  }

  removeAltName(altName: LocationOption): void {
    if (!this.localData.names || !this.canRemoveName(altName)) return;
    const index = this.localData.names.indexOf(altName);
    if (index !== -1) {
      this.localData.names.splice(index, 1);
    }
  }

  get canSave(): boolean {
    return !!this.selectedDefaultName?.name?.trim()
      && !!this.localData.category
      && (!this.isOverseas || !!this.localData.countryId);
  }

  closeDialog(data?: LocationDetails): void {
    if (data) {
      data.defaultNameId = this.selectedDefaultName?.nameId ?? undefined;
      if (data.names) {
        for (const name of data.names) {
          name.inRegion = data.inRegion;
          name.category = data.category;
        }
      }
    }
    this.ref.close(data);
  }
}
